import * as React from 'react';
import * as cx from 'classnames';
import SuperCritical, { SuperCriticalComponent, SuperCriticalReference } from 'lib/supercritical';
import { noDefault } from 'lib/eventWrappers';

import { NewComment, CommentRecipient } from './newcomment';

import { PersonProfilePopupView } from 'portcall/personprofilepopup/view';
import { UploadFileView } from 'portcall/uploadfile/view';
import { UserSelectorView } from 'portcall/userselector/view';

export interface NewCommentProps {
    model : NewComment;
    topLevel? : boolean;
    allowTaggingUsers? : boolean;
}

export const NewCommentView = SuperCritical(({
    model: m,
    topLevel,
    allowTaggingUsers = true
} : NewCommentProps) =>
    <div className={cx("newcomment", { "top-level": topLevel })}>
        <CommentMainRow model={m} allowTaggingUsers={allowTaggingUsers} />
        <div className="clearfix">
            <RecipientSelectorView model={m} />
            <AddedAttachmentView model={m} />
        </div>
        <RecipientsView model={m} />
    </div>
);

const CommentMainRow = SuperCritical(({ model: m, allowTaggingUsers } : {
    model : NewComment,
    allowTaggingUsers : boolean
}) =>
    <div className="newcomment-main-row">
        <CommentMessageView model={m} />
        <CommentActions model={m} allowTaggingUsers={allowTaggingUsers} />
    </div>
);

class CommentMessageView extends SuperCriticalComponent<{ model : NewComment }> {
    readonly messageRef : (el : HTMLTextAreaElement | null) => void;

    constructor(props : { model : NewComment }) {
        super(props);
        this.messageRef = SuperCriticalReference(this, ({ model }, el) => model.MessageFocusTriggered() && el.focus());
    }

    srender({ model: m } : { model : NewComment }) {
        return (
            <textarea
                className="message-input"
                rows={1}
                placeholder="Add Comment..."
                value={m.NewComment().Message() ?? ''}
                onChange={e => m.NewComment().Message(e.currentTarget.value)}
                ref={this.messageRef}
            />
        );
    }
}

const CommentActions = SuperCritical(({ model: m, allowTaggingUsers } : {
    model : NewComment,
    allowTaggingUsers : boolean
}) =>
    <span className="newcomment-actions">
        {allowTaggingUsers && <RecipientsIcon model={m} />}
        <AttachmentIcon model={m} />
        <PostButton model={m} />
        <UploadFileView model={m.FileUploader} />
    </span>
);

const RecipientsIcon = SuperCritical(({ model: m } : { model : NewComment }) =>
    <button
        className="pc-button small-button"
        onClick={noDefault(m.RecipientSelector().Focus)}
    >
        <i className="fa fa-user" />
    </button>
);

const AttachmentIcon = SuperCritical(({ model: m } : { model : NewComment }) => {
    const hasAttachment = !!m.NewComment().AttachmentUrl();
    return (
        <label
            // "Disable" attachment icon if we already have an attachment
            className={cx("pc-button small-button", {
                "pc-button-disabled": hasAttachment
            })}
            htmlFor={hasAttachment ? undefined : m.FileUploader.FileInputId()}
        >
            <i className="fa fa-paperclip" />
        </label>
    );
});

const PostButton = SuperCritical(({ model: m } : { model : NewComment }) =>
    <button
        className="pc-button post-button"
        disabled={!m.CanSubmit()}
        onClick={noDefault(m.Submit)}
    >
        Post
    </button>
);

const AddedAttachmentView = SuperCritical(({ model: m } : { model : NewComment }) =>
    m.NewComment().AttachmentUrl() ?
        <div className="attachment-filename-shell">
            <span className="attachment-filename pc-text-fixedwidth">{m.NewComment().AttachmentFileName()}</span>
            <a
                href=""
                className="fa fa-remove"
                onClick={noDefault(() => m.NewComment().AttachmentUrl(null))}
            />
        </div> :
        null
);

class RecipientSelectorView extends SuperCriticalComponent<{ model : NewComment }> {
    readonly inputRef : (el : HTMLInputElement | null) => void;

    constructor(props : { model : NewComment }) {
        super(props);
        this.inputRef = SuperCriticalReference(this, ({ model }, el) => model.RecipientSelector().SelectorFocusTriggered() && el.focus());
    }

    srender({ model: m } : { model : NewComment }) {
        return m.RecipientSelector().IsVisible() ?
            <UserSelectorView
                model={m.RecipientSelector().Selector}
                placeholder="Add a recipient"
                inputRef={this.inputRef}
            /> :
            null;
    }
}

const RecipientsView = SuperCritical(({ model: m } : { model : NewComment }) =>
    m.Recipients().length > 0 ?
        <div className="recipients">
            {m.Recipients().map(r => <RecipientView recipient={r} key={r.User.Id()} />)}
        </div> :
        null
);

const RecipientView = SuperCritical(({ recipient } : { recipient : CommentRecipient }) =>
    <div className="recipient-shell">
        <a
            href=""
            className="recipient-name pc-text-fixedwidth"
            onClick={noDefault(recipient.ToggleProfile)}
        >
            {recipient.User.FullName()}
        </a>
        <a href="" className="fa fa-remove" onClick={noDefault(recipient.Delete)} />
        <PersonProfilePopupView modelContainer={recipient.ProfilePopup} />
    </div>
);



// WEBPACK FOOTER //
// ./apps/portcall/eventlog/newcommentview.tsx